/**
 * Centralised TanStack Query key factory.
 *
 * One place for every query key keeps invalidation correct and refactor-safe:
 * a mutation invalidates `queryKeys.suppliers.list(facility)` rather than a
 * stringly-typed array that can drift. Keys are hierarchical
 * (`["suppliers", "list", facility]`) so a broad `["suppliers"]` invalidation
 * clears every supplier query at once when needed.
 *
 * Convention: `<domain>.all` is the broad prefix; `.list(...)` / `.detail(...)`
 * are the specific keys. Always include the facility — all Eclipse data is
 * facility-scoped (multi-tenant), so keys must be too or one facility's cache
 * would leak into another.
 */
export const queryKeys = {
  suppliers: {
    all: ["suppliers"] as const,
    list: (facility: string) => ["suppliers", "list", facility] as const,
    detail: (facility: string, supplierCode: string) =>
      ["suppliers", "detail", facility, supplierCode] as const,
    metrics: (facility: string, supplierCode: string) =>
      ["suppliers", "metrics", facility, supplierCode] as const,
    recentPos: (facility: string, supplierCode: string) =>
      ["suppliers", "recent-pos", facility, supplierCode] as const,
    items: (facility: string, supplierCode: string) =>
      ["suppliers", "items", facility, supplierCode] as const,
  },
  items: {
    all: ["items"] as const,
    list: (facility: string, types: string) => ["items", "list", facility, types] as const,
    detail: (facility: string, itemCode: string) => ["items", "detail", facility, itemCode] as const,
    poHistory: (facility: string, itemCode: string) =>
      ["items", "po-history", facility, itemCode] as const,
    reorderConfig: (facility: string, itemCode: string) =>
      ["items", "reorder-config", facility, itemCode] as const,
    suppliers: (facility: string, itemCode: string) =>
      ["items", "suppliers", facility, itemCode] as const,
  },
  workOrders: {
    all: ["work-orders"] as const,
    list: (facility: string) => ["work-orders", "list", facility] as const,
    detail: (facility: string, workOrderId: string) =>
      ["work-orders", "detail", facility, workOrderId] as const,
  },
  trace: {
    backward: (facility: string, lot: string, item: string) =>
      ["trace", "backward", facility, lot, item] as const,
    forward: (facility: string, lot: string, item: string) =>
      ["trace", "forward", facility, lot, item] as const,
  },
  accounting: {
    costSummary: (facility: string) => ["accounting", "cost-summary", facility] as const,
  },
  storeOrders: {
    list: (facility: string) => ["store-orders", "list", facility] as const,
    byDeliveryDate: (facility: string, deliveryDate: string) =>
      ["store-orders", "list", facility, "delivery", deliveryDate] as const,
  },
  pickRecommendation: (facility: string, itemCode: string, quantity: string) =>
    ["pick-recommendation", facility, itemCode, quantity] as const,
  recipes: {
    list: (facility: string) => ["recipes", "list", facility] as const,
    detail: (facility: string, recipeCode: string) =>
      ["recipes", "detail", facility, recipeCode] as const,
  },
  mrp: {
    run: (facility: string, runId?: string) =>
      ["mrp", "run", facility, runId ?? "latest"] as const,
    suggestions: (facility: string, runId: string) =>
      ["mrp", "suggestions", facility, runId] as const,
  },
  productionReadiness: (facility: string, date: string) =>
    ["production-readiness", facility, date] as const,
  inventory: {
    list: (facility: string) => ["inventory", "list", facility] as const,
    expiring: (facility: string, days: number) =>
      ["inventory", "expiring", facility, days] as const,
    movements: (facility: string, filtersKey: string, token: string) =>
      ["inventory", "movements", facility, filtersKey, token] as const,
  },
  locations: {
    list: (facility: string) => ["locations", "list", facility] as const,
  },
  dashboard: (facility: string) => ["dashboard", facility] as const,
  purchaseOrders: {
    detail: (poNumber: string) => ["purchase-orders", "detail", poNumber] as const,
  },
  receipts: {
    detail: (facility: string, receiptId: string) =>
      ["receipts", "detail", facility, receiptId] as const,
  },
  invoices: {
    detail: (facility: string, invoiceNumber: string) =>
      ["invoices", "detail", facility, invoiceNumber] as const,
  },
  finance: {
    all: ["finance"] as const,
    accounts: (type: string) => ["finance", "accounts", type] as const,
    journal: (facility: string, period: string) => ["finance", "journal", facility, period] as const,
    journalEntry: (facility: string, entryId: string) =>
      ["finance", "journal", "detail", facility, entryId] as const,
    trialBalance: (facility: string, period: string) => ["finance", "trial-balance", facility, period] as const,
    apAging: (facility: string) => ["finance", "ap-aging", facility] as const,
    arAging: (facility: string) => ["finance", "ar-aging", facility] as const,
    salesInvoices: (facility: string, status: string) => ["finance", "sales-invoices", facility, status] as const,
    salesInvoice: (facility: string, invoiceNumber: string) =>
      ["finance", "sales-invoices", "detail", facility, invoiceNumber] as const,
    periods: (facility: string) => ["finance", "periods", facility] as const,
  },
} as const
